import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'motion/react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import Dica from '@/components/feature/Dica';
import CheckAnimado from '@/components/movimento/CheckAnimado';
import { useModoEquipe } from '@/components/feature/ModoEquipe';
import LeitorVale from './components/LeitorVale';
import ModalEntregas from './components/ModalEntregas';
import ProdutosBarraca from './components/ProdutosBarraca';
import { iniciarAudio, tocarBipes, vibrar } from '@/pages/portaria/utils';
import type { ConferirVale, MeuPonto } from '@/pages/loja/types';

type Estado = 'carregando' | 'escolher-ponto' | 'pronto' | 'sem-acesso' | 'erro';

// Cor da tela conforme a situação devolvida pelo banco.
type CorTela = 'verde' | 'vermelho' | 'amarelo';

function corDaSituacao(situacao: string): CorTela {
  if (situacao === 'liberado') return 'verde';
  if (situacao === 'outra_barraca' || situacao === 'expirado' || situacao === 'e_carteira') {
    return 'amarelo';
  }
  return 'vermelho';
}

export default function LojaBarracaPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, pronto } = useAuth();
  const { ativo: modoEquipe } = useModoEquipe();

  const [estado, setEstado] = useState<Estado>('carregando');
  const [pontos, setPontos] = useState<MeuPonto[]>([]);
  const [pontoId, setPontoId] = useState<number | null>(null);
  const [pontoNome, setPontoNome] = useState('');
  const [eventoId, setEventoId] = useState<number | null>(null);
  const [pontoAberto, setPontoAberto] = useState(true);
  const [pausando, setPausando] = useState(false);
  const [mostrarProdutos, setMostrarProdutos] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const [resultado, setResultado] = useState<ConferirVale | null>(null);
  const [entregando, setEntregando] = useState(false);
  const [entregue, setEntregue] = useState<string | null>(null);
  const [erroCamera, setErroCamera] = useState<string | null>(null);
  const [erroTransitorio, setErroTransitorio] = useState<string | null>(null);
  const [modoPedido, setModoPedido] = useState(false);
  const [numeroDigitado, setNumeroDigitado] = useState('');
  const [modalEntregas, setModalEntregas] = useState(false);

  const processandoRef = useRef(false);
  const entregueTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Mantém a tela acesa enquanto o operador escaneia (mesma técnica da portaria).
  useEffect(() => {
    let lock: { release?: () => Promise<void> } | null = null;

    async function adquirir() {
      try {
        const wl = navigator as Navigator & {
          wakeLock?: { request: (t: string) => Promise<{ release?: () => Promise<void> }> };
        };
        lock = await wl.wakeLock?.request('screen');
      } catch {
        // aparelho/navegador sem suporte
      }
    }

    adquirir();
    const aoVisivel = () => {
      if (document.visibilityState === 'visible') adquirir();
    };
    document.addEventListener('visibilitychange', aoVisivel);

    return () => {
      document.removeEventListener('visibilitychange', aoVisivel);
      lock?.release?.();
    };
  }, []);

  // O som só toca depois do primeiro toque na tela (exigência dos celulares).
  useEffect(() => {
    const aoPrimeiroToque = () => iniciarAudio();
    document.addEventListener('pointerdown', aoPrimeiroToque, { once: true });
    return () => document.removeEventListener('pointerdown', aoPrimeiroToque);
  }, []);

  useEffect(() => {
    if (!pronto) return;
    if (!user) {
      navigate(`/entrar?voltar=${encodeURIComponent('/loja/barraca')}`, { replace: true });
      return;
    }
    let ativo = true;
    async function carregar() {
      setEstado('carregando');
      setErro(null);
      try {
        const { data, error } = await supabase.rpc('loja_meus_pontos');
        if (error) throw error;
        if (!ativo) return;
        const lista = (data as MeuPonto[]) || [];
        setPontos(lista);
        if (lista.length === 0) {
          setEstado('sem-acesso');
          return;
        }

        // Decide qual ponto abrir: ?ponto= > localStorage > único ponto.
        let escolhido: MeuPonto | null = null;
        const pontoDaUrl = Number(searchParams.get('ponto'));
        if (
          Number.isFinite(pontoDaUrl) &&
          pontoDaUrl > 0 &&
          lista.some((p) => p.ponto_id === pontoDaUrl)
        ) {
          escolhido = lista.find((p) => p.ponto_id === pontoDaUrl) ?? null;
        } else {
          const salvo = Number(localStorage.getItem('barraca:ponto'));
          if (
            Number.isFinite(salvo) &&
            salvo > 0 &&
            lista.some((p) => p.ponto_id === salvo)
          ) {
            escolhido = lista.find((p) => p.ponto_id === salvo) ?? null;
          } else if (lista.length === 1) {
            escolhido = lista[0];
          }
        }

        if (escolhido) {
          definirPonto(escolhido);
        } else {
          setEstado('escolher-ponto');
        }
      } catch (e) {
        if (ativo) {
          setErro(mensagemAmigavel(e, 'Não foi possível carregar.'));
          setEstado('erro');
        }
      }
    }
    carregar();
    return () => {
      ativo = false;
    };
  }, [navigate, searchParams, pronto, user]);

  useEffect(() => {
    return () => {
      if (entregueTimeoutRef.current) clearTimeout(entregueTimeoutRef.current);
    };
  }, []);

  function definirPonto(p: MeuPonto) {
    setPontoId(p.ponto_id);
    setPontoNome(p.ponto_nome);
    setEventoId(p.evento_id ?? null);
    localStorage.setItem('barraca:ponto', String(p.ponto_id));
    setEstado('pronto');
    void carregarEstadoPonto(p.ponto_id, p.evento_id ?? null);
  }

  function escolherPonto(p: MeuPonto) {
    definirPonto(p);
  }

  async function carregarEstadoPonto(pid: number, eid: number | null) {
    setPontoAberto(true);
    if (eid == null) return;
    try {
      const { data } = await supabase.rpc('loja_painel', { p_evento: eid });
      const porPonto =
        ((data as { por_ponto?: { ponto_id: number; aberto: boolean }[] } | null)?.por_ponto) ?? [];
      const p = porPonto.find((x) => x.ponto_id === pid);
      if (p) setPontoAberto(p.aberto);
    } catch {
      try {
        const { data } = await supabase
          .from('loja_pontos')
          .select('aberto')
          .eq('id', pid)
          .maybeSingle();
        if (data) setPontoAberto((data as { aberto: boolean }).aberto);
      } catch {
        /* padrão: aberta */
      }
    }
  }

  async function alternarPausa() {
    if (pontoId == null) return;
    setPausando(true);
    try {
      const { error } = await supabase.rpc('loja_pausar_ponto', {
        p_ponto: pontoId,
        p_pausar: pontoAberto,
      });
      if (error) throw error;
      setPontoAberto((v) => !v);
    } catch (e) {
      setErroTransitorio(mensagemAmigavel(e, 'Não foi possível pausar.'));
      setTimeout(() => setErroTransitorio(null), 3000);
    } finally {
      setPausando(false);
    }
  }

  async function processarCodigo(lido: string) {
    if (processandoRef.current || pontoId == null) return;
    const codigo = lido.trim();
    if (!codigo) return;
    processandoRef.current = true;

    try {
      const { data, error } = await supabase.rpc(
        modoPedido ? 'loja_conferir_pedido' : 'loja_conferir_vale',
        { p_codigo: codigo, p_ponto: pontoId },
      );
      if (error) throw error;
      const r = (Array.isArray(data) ? data[0] : data) as ConferirVale | null;
      if (r) {
        setResultado(r);
        const cor = corDaSituacao(r.situacao);
        if (cor === 'verde') {
          tocarBipes(1, 880);
          vibrar(120);
        } else if (cor === 'vermelho') {
          tocarBipes(2, 440);
          vibrar([120, 60, 120]);
        } else {
          tocarBipes(2, 660);
          vibrar(200);
        }
      }
      setNumeroDigitado('');
    } catch {
      setErroTransitorio('Sem sinal — tente de novo');
      setTimeout(() => setErroTransitorio(null), 2500);
    } finally {
      processandoRef.current = false;
    }
  }

  const aoLerQR = useCallback((codigo: string) => {
    void processarCodigo(codigo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modoPedido, pontoId]);

  async function entregar() {
    if (!resultado?.vale_codigo || pontoId == null) return;
    setEntregando(true);
    try {
      const { error } = await supabase.rpc('loja_entregar_vale', {
        p_codigo: resultado.vale_codigo,
        p_ponto: pontoId,
      });
      if (error) throw error;
      const cliente = resultado.cliente || 'cliente';
      setEntregue(cliente);
      if (typeof navigator !== 'undefined' && 'vibrate' in navigator) navigator.vibrate?.(120);
      tocarBipes(1, 880);
      setResultado(null);
      entregueTimeoutRef.current = setTimeout(() => setEntregue(null), 1500);
    } catch (e) {
      setErroTransitorio(mensagemAmigavel(e, 'Não foi possível entregar.'));
      setTimeout(() => setErroTransitorio(null), 3000);
    } finally {
      setEntregando(false);
    }
  }

  function confirmarNumero() {
    if (numeroDigitado.trim()) processarCodigo(numeroDigitado);
  }

  // ---- Estados ----
  if (estado === 'carregando') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-100">
        <span className="flex h-12 w-12 items-center justify-center text-primary-500">
          <i className="ri-loader-4-line animate-spin text-4xl" />
        </span>
      </div>
    );
  }

  if (estado === 'erro') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-100 px-4 py-8">
        <div className="w-full max-w-[420px] overflow-hidden rounded-2xl border border-secondary-200 bg-white">
          <div className="h-[10px] w-full bg-primary-500" />
          <div className="p-8 text-center">
            <h1 className="text-xl font-medium text-foreground-950">Não foi possível carregar</h1>
            <p className="mt-2 text-sm text-foreground-600">{erro}</p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-6 w-full cursor-pointer whitespace-nowrap rounded-xl bg-primary-500 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
            >
              Tentar de novo
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (estado === 'sem-acesso') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-100 px-4 py-8">
        <div className="w-full max-w-[420px] overflow-hidden rounded-2xl border border-secondary-200 bg-white">
          <div className="h-[10px] w-full bg-primary-500" />
          <div className="p-8 text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
              <i className="ri-store-2-line text-2xl" />
            </span>
            <h1 className="mt-4 text-xl font-medium text-foreground-950">
              Você não é operador de nenhuma barraca
            </h1>
            <p className="mt-2 text-sm text-foreground-600">
              Peça ao organizador do evento para te convidar como operador.
            </p>
            {!modoEquipe && (
              <button
                type="button"
                onClick={() => navigate('/')}
                className="mt-6 w-full cursor-pointer whitespace-nowrap rounded-xl bg-primary-500 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
              >
                Voltar à vitrine
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (estado === 'escolher-ponto') {
    return (
      <div className="min-h-screen bg-background-100">
        <div className="mx-auto w-full max-w-[480px] px-4 py-10">
          <h1 className="text-center text-xl font-medium text-foreground-950">
            Em qual barraca você está?
          </h1>
          <p className="mt-1 text-center text-sm text-foreground-600">
            Escolha o ponto de retirada para começar a ler os vales.
          </p>
          <div className="mt-6 space-y-3">
            {pontos.map((p) => (
              <button
                key={p.ponto_id}
                type="button"
                onClick={() => escolherPonto(p)}
                className="flex w-full cursor-pointer items-center justify-between rounded-2xl border border-secondary-200 bg-white px-5 py-4 text-left transition-colors hover:border-primary-300"
              >
                <div>
                  <p className="text-base font-medium text-foreground-950">{p.ponto_nome}</p>
                  {p.evento && (
                    <p className="text-xs text-foreground-500">{p.evento}</p>
                  )}
                </div>
                <span className="flex h-5 w-5 items-center justify-center text-foreground-400">
                  <i className="ri-arrow-right-s-line text-xl" />
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ---- Tela principal (câmera + número) ----
  return (
    <div className="flex h-[100dvh] flex-col bg-background-100">
      {/* Topo */}
      <div className="shrink-0 border-b border-secondary-200 bg-white px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs text-foreground-500">Operando em</p>
            <div className="flex items-center gap-2">
              <h1 className="min-w-0 truncate text-base font-semibold text-foreground-950">
                {pontoNome}
              </h1>
              <Dica id="barraca" />
            </div>
          </div>
          <button
            type="button"
            onClick={alternarPausa}
            disabled={pausando}
            className={`flex shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-semibold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
              pontoAberto ? 'bg-danger-500 hover:bg-danger-600' : 'bg-success-500 hover:bg-success-600'
            }`}
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className={pontoAberto ? 'ri-pause-line' : 'ri-play-line'} />
            </span>
            {pausando ? '…' : pontoAberto ? 'Pausar barraca' : 'Reabrir'}
          </button>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setMostrarProdutos(true)}
            className="cursor-pointer whitespace-nowrap rounded-lg border border-secondary-300 bg-white px-3 py-1.5 text-xs font-medium text-foreground-700 transition-colors hover:bg-background-100"
          >
            Produtos
          </button>
          {pontos.length > 1 && (
            <button
              type="button"
              onClick={() => setEstado('escolher-ponto')}
              className="cursor-pointer whitespace-nowrap rounded-lg border border-secondary-300 bg-white px-3 py-1.5 text-xs font-medium text-foreground-700 transition-colors hover:bg-background-100"
            >
              Trocar
            </button>
          )}
          <button
            type="button"
            onClick={() => setModalEntregas(true)}
            className="cursor-pointer whitespace-nowrap rounded-lg border border-secondary-300 bg-white px-3 py-1.5 text-xs font-medium text-foreground-700 transition-colors hover:bg-background-100"
          >
            Últimas entregas
          </button>
        </div>
      </div>

      {/* Câmera */}
      <div className="relative flex-1 overflow-hidden">
        {!resultado && !entregue && (
          <>
            {erroCamera ? (
              <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
                  <i className="ri-camera-off-line text-2xl" />
                </span>
                <p className="text-sm text-foreground-600">
                  Não foi possível abrir a câmera. Use o número do vale abaixo.
                </p>
              </div>
            ) : (
              <LeitorVale ativo={!resultado} aoLer={aoLerQR} aoErroCamera={(m) => setErroCamera(m)} />
            )}
          </>
        )}

        {erroTransitorio && (
          <div className="absolute left-0 right-0 top-0 z-20 bg-danger-600 px-4 py-3 text-center text-sm font-semibold text-white">
            {erroTransitorio}
          </div>
        )}

        {/* Confirmação de entrega */}
        {entregue && (
          <div className="fixed inset-0 z-50 flex items-end justify-center">
            <div className="absolute inset-0 bg-black/45" aria-hidden />
            <div className="folha-sobe relative z-10 w-full max-w-[560px] rounded-t-[28px] bg-success-600 px-6 pb-8 pt-5 text-center text-white">
              <div className="mx-auto h-1 w-10 rounded-full bg-white/40" />
              <span className="mx-auto mt-4 flex h-24 w-24 items-center justify-center rounded-full bg-white/20">
                <CheckAnimado tamanho={62} cor="#ffffff" fundo="transparent" />
              </span>
              <p className="mt-4 text-[34px] font-extrabold uppercase leading-none">Entregue</p>
              <p className="mt-1 text-[20px] font-semibold">para {entregue}</p>
            </div>
          </div>
        )}

        {/* Resultado da conferência */}
        {resultado && !entregue && (
          <ResultadoVale
            resultado={resultado}
            pontoNome={pontoNome}
            entregando={entregando}
            aoEntregar={entregar}
            aoFechar={() => setResultado(null)}
          />
        )}
      </div>

      {/* Rodapé: número do vale */}
      <div className="shrink-0 border-t border-secondary-200 bg-white px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <input
              type="text"
              inputMode="numeric"
              value={numeroDigitado}
              onChange={(e) => setNumeroDigitado(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="ou digite o número do vale"
              className="w-full rounded-xl border border-secondary-300 bg-white px-4 py-3 text-center text-lg font-semibold tracking-[0.2em] text-foreground-950 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
              onKeyDown={(e) => {
                if (e.key === 'Enter') confirmarNumero();
              }}
            />
          </div>
          <button
            type="button"
            onClick={confirmarNumero}
            disabled={!numeroDigitado.trim()}
            className="shrink-0 cursor-pointer whitespace-nowrap rounded-xl bg-primary-500 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600 disabled:bg-secondary-300"
          >
            Confirmar
          </button>
        </div>

        <button
          type="button"
          onClick={() => setModoPedido((v) => !v)}
          className={`mx-auto mt-2 block cursor-pointer whitespace-nowrap text-xs underline ${
            modoPedido ? 'text-primary-600' : 'text-foreground-400'
          }`}
        >
          {modoPedido ? 'Lendo QR de pedido antigo (toque para voltar)' : 'Ler QR de pedido antigo'}
        </button>
      </div>

      {modalEntregas && pontoId != null && (
        <ModalEntregas pontoId={pontoId} aoFechar={() => setModalEntregas(false)} />
      )}

      {mostrarProdutos && pontoId != null && (
        <ProdutosBarraca
          pontoId={pontoId}
          eventoId={eventoId}
          aoFechar={() => setMostrarProdutos(false)}
          aoSalvo={() => undefined}
        />
      )}
    </div>
  );
}

interface EstiloTela {
  fundo: string;
  texto: string;
  circulo: string;
  icone: string;
  titulo: string;
  quadro: string;
}

const ESTILO_POR_SITUACAO: Record<string, EstiloTela> = {
  liberado: { fundo: 'bg-green-600', texto: 'text-white', circulo: 'bg-white/20', icone: 'ri-check-line', titulo: 'PODE ENTREGAR', quadro: '' },
  ja_entregue: { fundo: 'bg-red-600', texto: 'text-white', circulo: 'bg-white/20', icone: 'ri-close-line', titulo: 'NÃO ENTREGAR', quadro: 'Esta ficha já foi entregue.' },
  cancelado: { fundo: 'bg-red-600', texto: 'text-white', circulo: 'bg-white/20', icone: 'ri-close-line', titulo: 'NÃO ENTREGAR', quadro: 'Esta ficha foi cancelada.' },
  nao_encontrado: { fundo: 'bg-red-600', texto: 'text-white', circulo: 'bg-white/20', icone: 'ri-close-line', titulo: 'NÃO ENTREGAR', quadro: 'Ficha não encontrada.' },
  sem_acesso: { fundo: 'bg-red-600', texto: 'text-white', circulo: 'bg-white/20', icone: 'ri-lock-line', titulo: 'NÃO ENTREGAR', quadro: 'Você não opera esta barraca.' },
  outra_barraca: { fundo: 'bg-amber-500', texto: 'text-amber-950', circulo: 'bg-black/10', icone: 'ri-map-pin-line', titulo: 'OUTRA BARRACA', quadro: '' },
  expirado: { fundo: 'bg-amber-500', texto: 'text-amber-950', circulo: 'bg-black/10', icone: 'ri-time-line', titulo: 'FICHA VENCIDA', quadro: 'Peça para gerar outra — leva 5 segundos.' },
  e_carteira: { fundo: 'bg-amber-500', texto: 'text-amber-950', circulo: 'bg-black/10', icone: 'ri-wallet-3-line', titulo: 'ISSO É A CARTEIRA', quadro: "Peça para tocar em 'Retirar' e mostrar o vale." },
};

const ESTILO_FALLBACK: EstiloTela = {
  fundo: 'bg-red-600',
  texto: 'text-white',
  circulo: 'bg-white/20',
  icone: 'ri-close-line',
  titulo: 'NÃO ENTREGAR',
  quadro: '',
};

function ResultadoVale({
  resultado,
  pontoNome,
  entregando,
  aoEntregar,
  aoFechar,
}: {
  resultado: ConferirVale;
  pontoNome: string;
  entregando: boolean;
  aoEntregar: () => void;
  aoFechar: () => void;
}) {
  const conhecida = resultado.situacao in ESTILO_POR_SITUACAO;
  const estilo = ESTILO_POR_SITUACAO[resultado.situacao] ?? ESTILO_FALLBACK;
  const cor = corDaSituacao(resultado.situacao);

  let textoQuadro = estilo.quadro;
  if (resultado.situacao === 'outra_barraca') {
    textoQuadro = `Esta ficha é da barraca ${resultado.ponto_certo}. Oriente a pessoa até lá. Nada foi entregue.`;
  } else if (!conhecida) {
    textoQuadro = resultado.mensagem;
  }
  const detalhe = resultado.mensagem && resultado.mensagem !== textoQuadro ? resultado.mensagem : null;
  const numero = resultado.numero ? `Ficha nº ${resultado.numero}` : null;

  const corBotao = cor === 'verde' ? 'text-green-700' : cor === 'amarelo' ? 'text-amber-800' : 'text-red-700';
  const rotuloBotao = cor === 'verde' ? 'ENTREGAR' : cor === 'amarelo' ? 'ENTENDI' : 'LER OUTRA';

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center">
      <div className="absolute inset-0 bg-black/45" aria-hidden />
      <div
        className={`folha-sobe relative z-10 flex max-h-[88dvh] w-full max-w-[560px] flex-col overflow-hidden rounded-t-[28px] ${estilo.fundo} ${estilo.texto}`}
      >
        <div className="mx-auto mt-3 h-1 w-10 rounded-full bg-white/40" />
        <p className="px-5 pt-3 text-center text-sm font-semibold opacity-[0.85]">
          {pontoNome} · lendo fichas
        </p>

        <div className="flex flex-1 flex-col items-center justify-center overflow-y-auto px-5 py-4">
        <motion.span
          className={`flex h-24 w-24 items-center justify-center rounded-full ${estilo.circulo}`}
          animate={cor === 'vermelho' ? { x: [0, -4, 4, -4, 4, 0] } : undefined}
          transition={{ duration: 0.2, ease: 'easeInOut' }}
        >
          {cor === 'amarelo' ? (
            <i className={`${estilo.icone} text-[56px]`} />
          ) : (
            <CheckAnimado
              estado={cor === 'vermelho' ? 'erro' : 'ok'}
              tamanho={62}
              cor="#ffffff"
              fundo="transparent"
            />
          )}
        </motion.span>

        <h2 className="mt-3 text-center text-4xl font-extrabold uppercase leading-tight">
          {estilo.titulo}
        </h2>

        {cor === 'verde' ? (
          <div
            className={`mt-4 w-full rounded-3xl bg-white p-4 text-foreground-950 ${
              resultado.itens.length > 3 ? 'max-h-[45vh] overflow-y-auto' : ''
            }`}
          >
            <div className="divide-y divide-secondary-200">
              {resultado.itens.map((item, i) => (
                <div key={i} className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0">
                  <span className="flex h-12 min-w-[48px] items-center justify-center rounded-xl bg-green-100 text-2xl font-extrabold text-green-800">
                    {item.quantidade}×
                  </span>
                  <span className="min-w-0 break-words text-3xl font-extrabold uppercase leading-tight text-foreground-950">
                    {item.nome}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="mt-4 w-full rounded-3xl bg-white p-4 text-foreground-950">
            <p className="text-xl font-bold">{textoQuadro}</p>
            {detalhe && <p className="mt-1 text-sm text-foreground-600">{detalhe}</p>}
            {numero && <p className="mt-1 text-sm text-foreground-600">{numero}</p>}
          </div>
        )}

        {cor === 'verde' && resultado.cliente && (
          <p className="mt-3 text-lg font-semibold">{resultado.cliente}</p>
        )}
        {cor === 'verde' && numero && <p className="mt-0.5 text-sm opacity-[0.85]">{numero}</p>}
      </div>

      <div className="p-4">
        <button
          type="button"
          onClick={cor === 'verde' ? aoEntregar : aoFechar}
          disabled={entregando}
          className={`flex h-[68px] w-full cursor-pointer items-center justify-center whitespace-nowrap rounded-2xl bg-white text-2xl font-extrabold transition-opacity disabled:opacity-70 ${corBotao}`}
        >
          {entregando ? (
            <span className="flex items-center justify-center gap-2">
              <i className="ri-loader-4-line animate-spin" />
              Entregando…
            </span>
          ) : (
            rotuloBotao
          )}
        </button>
        {cor === 'verde' && (
          <button
            type="button"
            onClick={aoFechar}
            className="mt-2 w-full cursor-pointer whitespace-nowrap text-center text-base font-semibold text-white/90"
          >
            Fechar
          </button>
        )}
        </div>
      </div>
    </div>
  );
}